/**
 * Circuit breaker interceptor for fault tolerance
 */

import { Interceptor, RpcEnvelope, RpcResponse } from "../types/protocol";

export enum CircuitBreakerState {
  CLOSED = "closed",
  OPEN = "open",
  HALF_OPEN = "half_open",
}

export interface CircuitBreakerConfig {
  failureThreshold: number; // Number of failures before opening
  recoveryTimeout: number; // Milliseconds to wait before trying again
  monitoringPeriod: number; // Period for tracking failures
}

export interface CircuitBreakerMetrics {
  state: CircuitBreakerState;
  failureCount: number;
  successCount: number;
  lastFailureTime: number | null;
  openedTime: number | null;
}

class CircuitBreaker {
  private state: CircuitBreakerState = CircuitBreakerState.CLOSED;
  private failureCount: number = 0;
  private successCount: number = 0;
  private lastFailureTime: number | null = null;
  private openedTime: number | null = null;
  private recoveryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private config: CircuitBreakerConfig,
    private onStateChange?: (state: CircuitBreakerState) => void,
  ) {}

  async execute<T>(
    operation: () => Promise<T>,
    failureCondition: (error: Error) => boolean,
  ): Promise<T> {
    if (this.state === CircuitBreakerState.OPEN) {
      throw new Error("Circuit breaker is open");
    }

    try {
      const result = await operation();
      this.onSuccess();
      return result;
    } catch (error) {
      if (failureCondition(error as Error)) {
        this.onFailure();
      }
      throw error;
    }
  }

  private onSuccess(): void {
    this.failureCount = 0;
    this.successCount++;

    if (this.state === CircuitBreakerState.HALF_OPEN) {
      this.setState(CircuitBreakerState.CLOSED);
    }
  }

  private onFailure(): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    if (this.state === CircuitBreakerState.HALF_OPEN) {
      this.setState(CircuitBreakerState.OPEN);
    } else if (this.failureCount >= this.config.failureThreshold) {
      this.setState(CircuitBreakerState.OPEN);
    }
  }

  private setState(newState: CircuitBreakerState): void {
    const oldState = this.state;
    this.state = newState;

    if (newState === CircuitBreakerState.OPEN) {
      this.openedTime = Date.now();
      this.scheduleRecovery();
    }

    if (oldState !== newState && this.onStateChange) {
      this.onStateChange(newState);
    }
  }

  private scheduleRecovery(): void {
    if (this.recoveryTimer) {
      clearTimeout(this.recoveryTimer);
    }

    this.recoveryTimer = setTimeout(() => {
      this.setState(CircuitBreakerState.HALF_OPEN);
    }, this.config.recoveryTimeout);
  }

  getState(): CircuitBreakerState {
    return this.state;
  }

  getMetrics(): CircuitBreakerMetrics {
    return {
      state: this.state,
      failureCount: this.failureCount,
      successCount: this.successCount,
      lastFailureTime: this.lastFailureTime,
      openedTime: this.openedTime,
    };
  }

  reset(): void {
    this.state = CircuitBreakerState.CLOSED;
    this.failureCount = 0;
    this.successCount = 0;
    this.lastFailureTime = null;
    this.openedTime = null;

    if (this.recoveryTimer) {
      clearTimeout(this.recoveryTimer);
      this.recoveryTimer = null;
    }
  }

  destroy(): void {
    if (this.recoveryTimer) {
      clearTimeout(this.recoveryTimer);
    }
  }
}

export class CircuitBreakerInterceptor implements Interceptor {
  public readonly name = "CircuitBreakerInterceptor";
  public readonly order = 30; // Run after validation but before auth

  private defaultConfig: CircuitBreakerConfig = {
    failureThreshold: 5,
    recoveryTimeout: 30000,
    monitoringPeriod: 60000,
  };

  private circuitBreakers = new Map<string, CircuitBreaker>();
  private serviceConfigs = new Map<string, CircuitBreakerConfig>();
  private failureCondition: (error: Error) => boolean = () => true;

  constructor(config?: Partial<CircuitBreakerConfig>) {
    if (config) {
      this.defaultConfig = { ...this.defaultConfig, ...config };
    }
  }

  /**
   * Configure circuit breaker for a specific service
   */
  configureService(
    serviceName: string,
    config: Partial<CircuitBreakerConfig>,
  ): void {
    this.serviceConfigs.set(serviceName, { ...this.defaultConfig, ...config });
  }

  /**
   * Set custom failure condition
   */
  setFailureCondition(condition: (error: Error) => boolean): void {
    this.failureCondition = condition;
  }

  async intercept(
    direction: "inbound" | "outbound",
    message: RpcEnvelope | RpcResponse,
    next: () => Promise<any>,
  ): Promise<any> {
    // Only apply to outbound RPC calls
    if (direction !== "outbound" || !this.isRpcCall(message)) {
      return next();
    }

    const serviceName = message.service || "default";
    const circuitBreaker = this.getCircuitBreaker(serviceName);

    return circuitBreaker.execute(next, this.failureCondition);
  }

  private getCircuitBreaker(serviceName: string): CircuitBreaker {
    if (!this.circuitBreakers.has(serviceName)) {
      const config = this.serviceConfigs.get(serviceName) || this.defaultConfig;
      const circuitBreaker = new CircuitBreaker(config);
      this.circuitBreakers.set(serviceName, circuitBreaker);
    }

    return this.circuitBreakers.get(serviceName)!;
  }

  private isRpcCall(
    message: RpcEnvelope | RpcResponse,
  ): message is RpcEnvelope {
    return "kind" in message && message.kind === "call";
  }

  /**
   * Get current state of circuit breaker
   */
  getState(serviceName: string = "default"): CircuitBreakerState {
    const circuitBreaker = this.circuitBreakers.get(serviceName);
    return circuitBreaker
      ? circuitBreaker.getState()
      : CircuitBreakerState.CLOSED;
  }

  /**
   * Get metrics for all circuit breakers
   */
  getMetrics(): Record<string, CircuitBreakerMetrics> {
    const metrics: Record<string, CircuitBreakerMetrics> = {};

    // Always include default metrics
    if (!this.circuitBreakers.has("default")) {
      metrics.default = {
        state: CircuitBreakerState.CLOSED,
        failureCount: 0,
        successCount: 0,
        lastFailureTime: null,
        openedTime: null,
      };
    }

    for (const [serviceName, circuitBreaker] of this.circuitBreakers) {
      metrics[serviceName] = circuitBreaker.getMetrics();
    }

    return metrics;
  }

  /**
   * Reset all circuit breakers
   */
  reset(serviceName?: string): void {
    if (serviceName) {
      const circuitBreaker = this.circuitBreakers.get(serviceName);
      if (circuitBreaker) {
        circuitBreaker.reset();
      }
    } else {
      for (const circuitBreaker of this.circuitBreakers.values()) {
        circuitBreaker.reset();
      }
    }
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    for (const circuitBreaker of this.circuitBreakers.values()) {
      circuitBreaker.destroy();
    }
    this.circuitBreakers.clear();
  }
}
